import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import toast, { Toaster } from 'react-hot-toast';
import { CheckCircle, AlertCircle, Info, Send, ShieldCheck, LogOut } from 'lucide-react';
import { useFillTimer } from '../../hooks/useFillTimer';
import { authAPI } from '../../utils/adminAPI';
import { enqueteDinamicaAPI, criarStorageDinamico } from '../../utils/enqueteDinamicaAPI';
import { celebrarVoto } from '../../utils/celebrarConfete';
import { classesTextoParaFundo } from '../../utils/corTextoContraste';
import { formatarDataEncerramento } from '../../utils/formatarDataEncerramento';
import SubheaderVoltar from '../common/SubheaderVoltar';
import HeaderPrefeitura from '../common/HeaderPrefeitura';
import FooterPublico from '../common/FooterPublico';
import PerguntaUnicaEscolha from './PerguntaUnicaEscolha';
import PerguntaMultiplaEscolha from './PerguntaMultiplaEscolha';
import PerguntaAberta from './PerguntaAberta';
import FluxoCartasPerguntas from './FluxoCartasPerguntas';
import ImagemCapaEnquete from './ImagemCapaEnquete';
import FundoPersonalizado from './FundoPersonalizado';
import CompartilharEnquete from './CompartilharEnquete';
import ResultadoEnqueteDinamica from '../ResultadoEnquete/ResultadoEnqueteDinamica';
import CardEmpilhavelScroll from '../common/CardEmpilhavelScroll';
import { useEhMobile } from '../../hooks/useEhMobile';

const COMPONENTE_POR_TIPO = {
  unica_escolha: PerguntaUnicaEscolha,
  multipla_escolha: PerguntaMultiplaEscolha,
  aberta: PerguntaAberta
};

const FormularioDinamico = () => {
  const { slug } = useParams();
  const storage = criarStorageDinamico(slug);

  const [carregando, setCarregando] = useState(true);
  const [naoEncontrada, setNaoEncontrada] = useState(false);
  const [bloqueadoAdmin, setBloqueadoAdmin] = useState(false);
  const [enquete, setEnquete] = useState(null);
  const [perguntas, setPerguntas] = useState([]);
  const [trackingToken, setTrackingToken] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [jaVotou, setJaVotou] = useState(false);
  const [estatisticas, setEstatisticas] = useState(null);
  // Enquete curta (2-5 opções, 1 pergunta) — alguém de verdade responde
  // rápido o bastante pra esbarrar em FILL_TIME_TOO_FAST (mínimo de 2s no
  // backend, ver MIN_FILL_TIME_MS em security.js) sem ser bot nenhum. Uma
  // tela de "preparando" segura o formulário fora de vista por 3-4s
  // enquanto o cronômetro (iniciarFormulario/startTimer) já está contando
  // por baixo — na hora que a pessoa consegue de fato clicar em algo, o
  // tempo mínimo já passou sozinho.
  const [preparando, setPreparando] = useState(true);
  const [duracaoPreparo, setDuracaoPreparo] = useState(3500);

  // shouldUnregister:false (default do RHF v7, explícito aqui de propósito)
  // é o que faz FluxoCartasPerguntas.js funcionar: cada card desmonta ao
  // avançar/voltar, e sem isso o valor já respondido seria perdido.
  const formMethods = useForm({ shouldUnregister: false });
  const ehMobile = useEhMobile();
  const { handleSubmit, register } = formMethods;
  const { startTimer, stopTimer, getCurrentTime, formatTime, isTimerActive } = useFillTimer();

  useEffect(() => {
    let ativo = true;

    enqueteDinamicaAPI.obterDefinicao(slug)
      .then((dados) => {
        if (!ativo) return;
        setEnquete(dados.enquete);
        setPerguntas(dados.perguntas || []);
        // Sem tela de aceite de termos — token/timer começam direto, igual
        // já funciona nas enquetes legadas (Portal, Telão do Brasil). Exceto
        // em rascunho (link de prévia) ou encerrada (não dá mais pra votar
        // de jeito nenhum, ver o bloco "jaEncerrada" abaixo que nem chega a
        // mostrar o formulário): gerar token e cronometrar preenchimento
        // não serve pra nada nesses dois casos — nem a tela de "preparando"
        // (não tem tempo mínimo pra defender ali, ninguém consegue votar).
        if (!storage.hasVoted() && dados.enquete.status === 'publicada') {
          iniciarFormulario();
          const atrasoPreparo = 3000 + Math.random() * 1000; // 3 a 4s
          setDuracaoPreparo(atrasoPreparo);
          setTimeout(() => { if (ativo) setPreparando(false); }, atrasoPreparo);
        } else {
          setPreparando(false);
        }
      })
      .catch((error) => {
        if (!ativo) return;
        if (error.response?.data?.code === 'ADMIN_SESSION_ACTIVE') {
          setBloqueadoAdmin(true);
        } else {
          setNaoEncontrada(true);
        }
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });

    if (storage.hasVoted()) {
      setJaVotou(true);
    }

    return () => { ativo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  // Decide se mostra resultado (imediato, só após encerrar, ou nunca) de
  // acordo com enquete.modo_resultado. Dispara quando a pessoa já votou
  // (seja porque acabou de votar, seja porque voltou à página depois) OU
  // quando a enquete já está encerrada (mesmo pra quem nunca votou — ver
  // bloco "jaEncerrada" mais abaixo, que troca o formulário pelo resultado
  // final direto, igual as enquetes legadas já fazem).
  useEffect(() => {
    if (!enquete) return;
    if (!jaVotou && enquete.status !== 'encerrada') return;
    if (enquete.modo_resultado === 'nao_mostrar') return;

    let ativo = true;
    enqueteDinamicaAPI.obterEstatisticas(slug)
      .then((stats) => { if (ativo) setEstatisticas(stats); })
      .catch(() => { /* 403 RESULT_HIDDEN/RESULT_NOT_YET_AVAILABLE: sem resultado, sem erro visível */ });

    return () => { ativo = false; };
  }, [jaVotou, enquete, slug]);

  // Confete dispara aqui, não dentro de onSubmit — cobre os dois jeitos de
  // chegar na tela de agradecimento (acabou de votar OU voltou depois numa
  // enquete que esta aba do navegador já tinha votado, via storage.hasVoted()
  // no useEffect de carregamento). `jaVotou` só transiciona de false pra
  // true nos dois casos (nunca começa como true), então isso roda exatamente
  // uma vez por sessão de tela, não fica repetindo a cada render. Espera
  // `enquete` carregar antes de decidir: sem isso, numa revisita a uma
  // enquete que fechou depois do voto, `jaVotou` pode ficar true antes de
  // `enquete.status` chegar, e o confete dispararia antes de saber que
  // devia ficar quieto (ver `encerrada` logo abaixo, mesmo raciocínio pro
  // card "Obrigado").
  useEffect(() => {
    if (jaVotou && enquete && enquete.status !== 'encerrada') celebrarVoto();
  }, [jaVotou, enquete]);

  const handleLogoutEVoltar = async () => {
    await authAPI.logout();
    window.location.reload();
  };

  const iniciarFormulario = async () => {
    try {
      let token = storage.getTrackingToken();
      if (!token) {
        const resposta = await enqueteDinamicaAPI.gerarToken(slug);
        token = resposta.token;
        storage.saveTrackingToken(token);
      }
      setTrackingToken(token);
      startTimer();
    } catch (error) {
      toast.error('Erro ao iniciar formulário. Recarregue a página.');
    }
  };

  const onSubmit = async (dados) => {
    if (enviando) return;
    setEnviando(true);

    try {
      const tempo = stopTimer();
      const payload = {
        token_rastreamento: trackingToken,
        tempo_preenchimento_ms: tempo,
        respostas: dados.respostas || {},
        website_url: dados.website_url || ''
      };

      await enqueteDinamicaAPI.registrarVoto(slug, payload);
      storage.markAsVoted();
      storage.clearTrackingToken();
      setJaVotou(true);
      toast.success('Voto registrado com sucesso! Obrigado pela participação.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      if (error.response?.status === 409) {
        storage.markAsVoted();
        storage.clearTrackingToken();
        setJaVotou(true);
        toast.success('Obrigado! Seu voto já havia sido registrado.');
      } else if (error.response?.status === 429) {
        toast.error('Aguarde alguns segundos antes de tentar novamente.');
      } else if (error.response?.status === 400 && error.response?.data?.details) {
        toast.error(error.response.data.details[0] || error.userMessage);
      } else {
        toast.error(error.userMessage || 'Erro ao registrar voto. Tente novamente.');
      }
    } finally {
      setEnviando(false);
    }
  };

  if (carregando) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="loading-spinner" />
      </div>
    );
  }

  if (bloqueadoAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <HeaderPrefeitura />
        <SubheaderVoltar />
        <div className="flex-1 flex items-center justify-center px-4">
        <div className="card max-w-md text-center">
          <ShieldCheck className="w-12 h-12 text-primary-600 mx-auto mb-3" />
          <h1 className="text-lg font-bold text-gray-900 mb-2">Você está logado como administrador</h1>
          <p className="text-gray-600 mb-4">
            Para votar ou ver esta enquete como visitante, saia da sua sessão de admin primeiro.
          </p>
          <button onClick={handleLogoutEVoltar} className="btn-primary inline-flex items-center gap-2">
            <LogOut className="w-4 h-4" /> Sair da sessão de admin
          </button>
        </div>
        </div>
        <FooterPublico />
      </div>
    );
  }

  if (naoEncontrada) {
    return (
      <div className="min-h-screen flex flex-col">
        <HeaderPrefeitura />
        <SubheaderVoltar />
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center">
            <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-600">Enquete não encontrada.</p>
          </div>
        </div>
        <FooterPublico />
      </div>
    );
  }

  // Só pra texto solto fora de card branco (título/descrição da enquete,
  // "X pessoas já responderam" dentro de ResultadoEnqueteDinamica) — texto
  // dentro de card continua com as cores fixas de sempre, o fundo branco do
  // card já garante contraste bom não importa qual seja o fundo da página.
  const corTexto = classesTextoParaFundo(enquete?.fundo_tipo, enquete?.fundo);
  // Enquete pode fechar (auto-encerramento, ver data_encerramento) antes de
  // alguém nunca ter votado, ou entre o momento do voto e uma revisita —
  // nos dois casos "Obrigado pela participação"/formulário não fazem mais
  // sentido, só o resultado final importa. Mesmo padrão visual já usado nas
  // enquetes legadas (Portal, Telão do Brasil — ver FormularioEnquete.js/
  // FormularioTelaBrasil.js) pra consistência, com a data de verdade da
  // enquete em vez de uma data fixa no código como as legadas têm.
  const jaEncerrada = enquete?.status === 'encerrada';

  if (jaVotou || jaEncerrada) {
    return (
      <FundoPersonalizado enquete={enquete}>
        <Toaster position="top-center" />
        <HeaderPrefeitura />
        <SubheaderVoltar />
        <div className="px-4 py-10">
          {jaEncerrada ? (
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-red-50 border-2 border-red-200 px-6 py-3 rounded-full mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
                <span className="font-bold text-red-900">Enquete Encerrada</span>
              </div>
              {enquete?.data_encerramento && (
                <p className={corTexto?.corpo || 'text-gray-600'}>
                  Encerrado em <strong>{formatarDataEncerramento(enquete.data_encerramento)}</strong>
                </p>
              )}
            </div>
          ) : (
            <div className="card max-w-lg mx-auto text-center animate-fade-in-up mb-8">
              <CheckCircle className="w-14 h-14 text-green-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Obrigado pela sua participação!</h1>
              <p className="text-gray-600">Seu voto em "{enquete?.titulo}" foi registrado com sucesso.</p>
              <CompartilharEnquete titulo={enquete?.titulo} dataEncerramento={enquete?.data_encerramento} />
            </div>
          )}

          {estatisticas && <ResultadoEnqueteDinamica stats={estatisticas} corTexto={corTexto} />}
        </div>
        <div className="mt-auto"><FooterPublico /></div>
      </FundoPersonalizado>
    );
  }

  const isRascunho = enquete?.status === 'rascunho';

  return (
    <FundoPersonalizado enquete={enquete}>
      <Toaster position="top-center" />

      <HeaderPrefeitura isTimerActive={isTimerActive} tempoFormatado={formatTime(getCurrentTime())} />
      <SubheaderVoltar />

      <main className="max-w-2xl mx-auto p-6">
        <div className="text-center mb-8 animate-fade-in-up">
          <h1 className={`text-2xl sm:text-3xl font-bold ${corTexto?.titulo || 'text-gray-900'}`}>{enquete?.titulo}</h1>
          {enquete?.descricao && <p className={`mt-2 ${corTexto?.corpo || 'text-gray-600'}`}>{enquete.descricao}</p>}
        </div>

        {enquete?.imagem_capa && (
          <div className="mb-8">
            <ImagemCapaEnquete capa={enquete.imagem_capa} capaMobile={enquete.imagem_capa_mobile} />
          </div>
        )}

        {isRascunho && (
          <div className="flex items-start gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 mb-6">
            <Info className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <p>Prévia — esta enquete ainda não foi publicada. Dá pra conferir as perguntas, mas a votação só abre quando o status virar "Publicada".</p>
          </div>
        )}

        {preparando && !isRascunho ? (
          <div className="card text-center py-14 animate-fade-in-up">
            {/* 3 bolinhas saltitando — animate-bolinha-saltitar (ver
                index.css), não o animate-bounce do Tailwind: aquele usa
                translateY EM PORCENTAGEM da altura do próprio elemento
                (~3,5px numa bolinha de 14px, invisível na prática). Esse
                usa pixel fixo (-12px), padrão comprovado de "three dots
                loading" (pesquisado, não chutado dessa vez). */}
            <div className="flex items-center justify-center gap-2 mb-5">
              <span className="w-3.5 h-3.5 bg-primary-600 rounded-full animate-bolinha-saltitar" style={{ animationDelay: '0ms' }} />
              <span className="w-3.5 h-3.5 bg-primary-600 rounded-full animate-bolinha-saltitar" style={{ animationDelay: '200ms' }} />
              <span className="w-3.5 h-3.5 bg-primary-600 rounded-full animate-bolinha-saltitar" style={{ animationDelay: '400ms' }} />
            </div>
            <p className="text-gray-700 font-medium">Preparando sua enquete...</p>
            <p className="text-sm text-gray-400 mt-1 mb-5">Só um instante</p>
            {/* Barra determinada, sincronizada com o mesmo atraso aleatório
                (duracaoPreparo) que decide quando preparando vira false —
                mostra progresso de verdade em vez de só um indicador sem
                noção nenhuma de quanto falta. */}
            <div className="w-full max-w-[220px] mx-auto h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-primary-500 rounded-full"
                style={{ animation: `preparoProgresso ${duracaoPreparo}ms linear forwards` }}
              />
            </div>
          </div>
        ) : (
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <input
                {...register('website_url')}
                type="text"
                className="honeypot"
                tabIndex="-1"
                autoComplete="off"
              />

              {isRascunho ? (
                // Prévia do admin — visão geral de tudo de uma vez continua
                // fazendo mais sentido aqui do que o fluxo de cartas (quem
                // está revisando quer ver o conjunto, não simular o voto).
                <>
                  {perguntas.map((pergunta, index) => {
                    const Componente = COMPONENTE_POR_TIPO[pergunta.tipo];
                    if (!Componente) return null;
                    return <Componente key={pergunta.id} pergunta={pergunta} indice={index + 1} />;
                  })}
                  <div className="card text-center">
                    <button
                      type="submit"
                      disabled
                      title="Votação ainda não está aberta"
                      className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      <span>Votação ainda não disponível</span>
                    </button>
                  </div>
                </>
              ) : ehMobile ? (
                // Mobile: um card por vez, responde e ele voa pro lado (ver
                // FluxoCartasPerguntas.js).
                <FluxoCartasPerguntas perguntas={perguntas} corTexto={corTexto} enviando={enviando} />
              ) : (
                // Desktop: mesmo empilhamento por scroll da tela de
                // resultado (CardEmpilhavelScroll, ver components/common/) —
                // nada desaparece, então não tem "revisar respostas"
                // separado aqui: rolar pra cima já mostra/edita qualquer
                // resposta anterior a qualquer momento.
                <>
                  {perguntas.map((pergunta, index) => {
                    const Componente = COMPONENTE_POR_TIPO[pergunta.tipo];
                    if (!Componente) return null;
                    return (
                      <CardEmpilhavelScroll key={pergunta.id} indice={index}>
                        <Componente pergunta={pergunta} indice={index + 1} />
                      </CardEmpilhavelScroll>
                    );
                  })}
                  <div className="card text-center">
                    <button
                      type="submit"
                      disabled={enviando}
                      className="btn-primary w-full sm:w-auto inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {enviando ? (
                        <>
                          <div className="loading-spinner" />
                          <span>Enviando...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          <span>Enviar</span>
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}
            </form>
          </FormProvider>
        )}
      </main>
      <div className="mt-auto"><FooterPublico /></div>
    </FundoPersonalizado>
  );
};

export default FormularioDinamico;
